/* Accessibility fix: WCAG 4.1.2 Name, Role, Value
   A number of recurring interactive controls across the theme have no
   accessible name: mega-menu dropdown-caret buttons (.button-dropdown-icon)
   and popup/drawer close buttons (.button-close.close-popup). Both are
   icon-only buttons with no visible text and no aria-label, so a screen
   reader announces them as a bare "button" with no indication of what they
   do. These classes are reused across many template files (menu drawer,
   search modal, cart drawer, quick view, popups, etc.), so rather than
   hand-patch each template, this runs once on load (and again whenever
   Shopify re-renders a section, e.g. in the theme editor or after an
   AJAX cart update) and labels every matching button found in the DOM. */
(function () {
  function labelDropdownCaretButtons() {
    document.querySelectorAll('.button-dropdown-icon').forEach(function (btn) {
      if (btn.hasAttribute('aria-label')) return;
      var scope = btn.closest('.menu__item-title') || btn.parentElement;
      var hoverEl = scope ? scope.querySelector('[data-hover]') : null;
      var label = hoverEl
        ? (hoverEl.getAttribute('data-hover') || hoverEl.textContent || '').trim()
        : '';
      btn.setAttribute('aria-label', label ? 'Expand ' + label + ' submenu' : 'Expand submenu');
    });
  }

  function labelClosePopupButtons() {
    document.querySelectorAll('.button-close.close-popup').forEach(function (btn) {
      if (btn.hasAttribute('aria-label')) return;
      btn.setAttribute('aria-label', 'Close');
    });
  }

  function labelAll() {
    labelDropdownCaretButtons();
    labelClosePopupButtons();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', labelAll);
  } else {
    labelAll();
  }

  document.addEventListener('shopify:section:load', labelAll);
  document.addEventListener('cart:updated', labelAll);
  document.addEventListener('cart-drawer:open', labelAll);
})();

/* Accessibility fix: WCAG 4.1.1 Parsing
   HTML ids must be unique per page. A few recur because the same component
   renders more than once (desktop + mobile, header + footer, etc.):
   - id="contact_form" is hardcoded by Shopify's own {% form 'customer' %}
     Liquid tag itself, not by theme template code, so it can't be fixed by
     editing a template — every instance of that form on the page gets the
     same id straight from Shopify's engine.
   - Various slideshow/carousel components also reuse ids when the same
     section type appears more than once on a page.
   Rather than hunt down every occurrence individually, this runs once on
   load (and again after Shopify re-renders a section) and renames any
   duplicate id it finds, keeping the first occurrence untouched and
   appending a suffix to each later one so every id on the page is unique. */
function deduplicateIds() {
  var seen = {};
  document.querySelectorAll('[id]').forEach(function (el) {
    var id = el.id;
    if (!id) return;
    if (!seen[id]) {
      seen[id] = 1;
      return;
    }
    seen[id] += 1;
    el.id = id + '-dup' + seen[id];
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', deduplicateIds);
} else {
  deduplicateIds();
}
document.addEventListener('shopify:section:load', deduplicateIds);

/* Accessibility fix: WCAG 4.1.2 Name, Role, Value (desktop header dropdown carets)
   The desktop header's "Shop" (and similar) dropdown caret is rendered as a
   plain <div tabindex="0">, not a real button, with no role, no accessible
   name, and no aria-expanded. A live test confirmed keyboard users CAN
   already open the submenu by tabbing to this div and pressing Enter — the
   underlying click behavior works. What's missing is that a screen reader
   never announces what the control is or whether the submenu is currently
   open or closed (confirmed live with VoiceOver: it announces only "list
   items" with no indication of state).
   This deliberately does NOT touch the existing click/keydown handling in
   global.js, since that already works and global.js is large and easy to
   break by editing directly (as happened earlier today with the cart-link
   id change). Instead this only adds the missing semantics on top, and uses
   a MutationObserver to keep aria-expanded in sync with the .open class
   that the existing script already toggles — so this fix rides on top of
   proven-working behavior rather than replacing it. */
(function () {
  function labelHeaderCarets() {
    document.querySelectorAll('.menu__item-title--header > .dropdown-icon--first-level, .menu__item-title--collapsible > .dropdown-icon--first-level, .menu__item-title--second-level .dropdown-icon--second-level').forEach(function (caret) {
      if (caret.dataset.a11yEnhanced) return;
      caret.dataset.a11yEnhanced = 'true';

      var parentTitle = caret.closest('.menu__item-title');
      var link = parentTitle ? parentTitle.querySelector('[data-hover]') : null;
      var label = link ? (link.getAttribute('data-hover') || link.textContent || '').trim() : '';

      caret.setAttribute('role', 'button');
      caret.setAttribute('aria-haspopup', 'true');
      caret.setAttribute('aria-label', label ? 'Expand ' + label + ' submenu' : 'Expand submenu');
      caret.setAttribute('aria-expanded', parentTitle && parentTitle.classList.contains('open') ? 'true' : 'false');

      if (parentTitle) {
        var observer = new MutationObserver(function () {
          var isOpen = parentTitle.classList.contains('open');
          caret.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
          caret.setAttribute('aria-label', (isOpen ? 'Collapse ' : 'Expand ') + (label || 'submenu') + (label ? ' submenu' : ''));
        });
        observer.observe(parentTitle, { attributes: true, attributeFilter: ['class'] });
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', labelHeaderCarets);
  } else {
    labelHeaderCarets();
  }

  document.addEventListener('shopify:section:load', labelHeaderCarets);
})();


/* Accessibility fix: WCAG 2.1.2 No Keyboard Trap (product media gallery)
   The product page image gallery uses Swiper with loop:true (confirmed in
   global.js: "this.loop = sliderEl.dataset.loop === 'true'" feeding into
   swiperBaseConfiguration). Swiper's loop mode works by cloning slides
   internally to create a seamless infinite-scroll illusion — those clones
   are real DOM nodes and, without explicit handling, remain fully keyboard
   focusable. A user tabbing through the thumbnail list can end up cycling
   through real slides plus their clones indefinitely, unable to tab past
   the gallery into the rest of the page (confirmed by direct user testing).
   Fix: use the modern `inert` attribute on every Swiper-generated duplicate
   slide. `inert` removes an element AND all of its descendants from both
   the tab order and the accessibility tree in one step — more robust than
   manually hunting down every focusable child (links, buttons) inside each
   clone. Runs repeatedly via a MutationObserver, since Swiper creates and
   recreates duplicate slides dynamically as the carousel loops. */
(function () {
  function neutralizeSwiperDuplicates(root) {
    (root || document).querySelectorAll('.swiper-slide-duplicate').forEach(function (dupe) {
      if (dupe.hasAttribute('inert')) return;
      dupe.setAttribute('inert', '');
      dupe.setAttribute('aria-hidden', 'true');
      // Fallback for browsers without inert support: manually disable tabbing
      // on every focusable element inside the clone.
      dupe.querySelectorAll('a[href], button, [tabindex]').forEach(function (el) {
        if (!el.hasAttribute('data-original-tabindex') && el.hasAttribute('tabindex')) {
          el.setAttribute('data-original-tabindex', el.getAttribute('tabindex'));
        }
        el.setAttribute('tabindex', '-1');
      });
    });
  }

  function init() {
    neutralizeSwiperDuplicates();
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.classList && node.classList.contains('swiper-slide-duplicate')) {
            neutralizeSwiperDuplicates(node.parentElement);
          } else if (node.querySelector && node.querySelector('.swiper-slide-duplicate')) {
            neutralizeSwiperDuplicates(node);
          }
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* Accessibility fix: WCAG 2.2.2 Pause, Stop, Hide (scrolling promotion banners)
   The homepage's scrolling promotional text banners ("cookies with
   benefits", "smart ingredients", etc., and the press-logo ticker) auto-
   scroll continuously with no way for a user to stop them. The component
   (assets/global.js, class ScrollingPromotion) already has working
   scrollingPause() and scrollingPlay() methods that toggle a
   scrolling-promotion--paused class — but they're only ever called
   automatically by an IntersectionObserver when the element scrolls in or
   out of view. There is no user-operable control, which is exactly what
   this success criterion requires.
   This adds a small pause/play button to every scrolling-promotion element
   and calls the component's own existing methods directly, rather than
   reimplementing the pause behavior. */
(function () {
  function addPauseButton(el) {
    if (el.dataset.pauseButtonAdded) return;
    el.dataset.pauseButtonAdded = 'true';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'scrolling-promotion-pause-btn';
    btn.setAttribute('aria-pressed', 'false');
    btn.setAttribute('aria-label', 'Pause scrolling text');
    btn.innerHTML = '&#10073;&#10073;';

    btn.addEventListener('click', function () {
      var isPaused = el.classList.contains('scrolling-promotion--paused');
      if (isPaused) {
        if (typeof el.scrollingPlay === 'function') el.scrollingPlay();
        btn.setAttribute('aria-pressed', 'false');
        btn.setAttribute('aria-label', 'Pause scrolling text');
        btn.innerHTML = '&#10073;&#10073;';
      } else {
        if (typeof el.scrollingPause === 'function') el.scrollingPause();
        btn.setAttribute('aria-pressed', 'true');
        btn.setAttribute('aria-label', 'Resume scrolling text');
        btn.innerHTML = '&#9654;';
      }
    });

    var parent = el.closest('.section-scrolling-promotion') || el.parentElement;
    if (parent) {
      parent.style.position = parent.style.position || 'relative';
      parent.appendChild(btn);
    }
  }

  function addAllPauseButtons() {
    document.querySelectorAll('scrolling-promotion').forEach(addPauseButton);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', addAllPauseButtons);
  } else {
    addAllPauseButtons();
  }

  document.addEventListener('shopify:section:load', addAllPauseButtons);
})();

/* Accessibility fix: WCAG 4.1.2 Name, Role, Value (product page accordions)
   The Description, Ingredients, Nutritionals, and Allergens sections on
   product pages (and elsewhere) all use the same reusable accordion
   component (snippets/accordion.liquid, <accordion-block> custom element).
   The clickable toggle is a plain <div tabindex="0"> with no role, no
   aria-expanded, and no aria-controls. The underlying JS (global.js, class
   AccordionBlock) already handles open/close correctly via click, Enter,
   and Escape — toggling an "is-open" class — but never sets any ARIA state,
   so a screen reader user has no way to know whether a section is expanded
   or collapsed, or which content block belongs to which toggle.
   This adds role="button", aria-expanded, and aria-controls (generating a
   unique id for the content panel if it doesn't already have one), and
   keeps aria-expanded in sync via a MutationObserver watching the existing
   is-open class — without touching the working click/keyboard logic in
   global.js.

   Follow-up fix (added after live testing): the button state announcement
   worked correctly ("expanded"), but VoiceOver's continued forward
   navigation did not reliably pick up the newly revealed plain-text content
   (Description/Ingredients/etc. are not interactive elements, so the
   existing focusElements()/blurElements() methods in global.js, which only
   manage tabindex on links/inputs/buttons, don't help here — this is a
   known behavior with dynamically revealed non-interactive content on
   VoiceOver, especially on iOS). Fix: when a panel opens, move focus
   directly onto the panel content itself, which forces the screen reader
   to land on and read it immediately rather than relying on the user to
   keep navigating forward and hope to encounter it. */
(function () {
  var panelIdCounter = 0;

  function enhanceAccordion(toggle) {
    if (toggle.dataset.a11yEnhanced) return;
    toggle.dataset.a11yEnhanced = 'true';

    var summary = toggle.querySelector('.accordion__summary');
    var panel = toggle.querySelector('.accordion__panel');
    if (!summary || !panel) return;

    if (!panel.id) {
      panelIdCounter++;
      panel.id = 'AccordionPanel-' + panelIdCounter;
    }
    if (!panel.hasAttribute('tabindex')) {
      panel.setAttribute('tabindex', '-1');
    }

    summary.setAttribute('role', 'button');
    summary.setAttribute('aria-controls', panel.id);
    summary.setAttribute('aria-expanded', toggle.classList.contains('is-open') ? 'true' : 'false');

    var observer = new MutationObserver(function () {
      var isOpen = toggle.classList.contains('is-open');
      summary.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      if (isOpen) {
        // Move focus onto the revealed content so screen readers land on
        // and read it immediately, rather than relying on continued manual
        // navigation to discover plain, non-interactive text content.
        window.setTimeout(function () {
          panel.focus({ preventScroll: true });
        }, 50);
      }
    });
    observer.observe(toggle, { attributes: true, attributeFilter: ['class'] });
  }

  function enhanceAllAccordions() {
    document.querySelectorAll('.accordion-toggle').forEach(enhanceAccordion);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enhanceAllAccordions);
  } else {
    enhanceAllAccordions();
  }

  document.addEventListener('shopify:section:load', enhanceAllAccordions);
})();

/* Announce product-page quantity changes to screen readers.
   The cart drawer already announces via CartDrawer-LiveRegionText;
   the product page has no equivalent live region, so this adds one. */
(function () {
  function getRegion() {
    var region = document.getElementById('ProductQuantityLiveRegion');
    if (!region) {
      region = document.createElement('div');
      region.id = 'ProductQuantityLiveRegion';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      region.className = 'visually-hidden';
      document.body.appendChild(region);
    }
    return region;
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('button[name="plus"], button[name="minus"]');
    if (!button) return;
    if (button.closest('cart-drawer, #CartDrawer')) return; // cart already announces

    var wrapper = button.closest('.quantity');
    var input = wrapper && wrapper.querySelector('input[type="number"]');
    if (!input) return;

    setTimeout(function () {
      var region = getRegion();
      region.textContent = '';
      setTimeout(function () {
        region.textContent = 'Quantity updated to ' + input.value + '.';
      }, 50);
    }, 150);
  });
})();

/* Accessibility fix: WCAG 2.2.2 Pause, Stop, Hide (looping videos)
   Looping theme videos (e.g. the homepage "Cookies for every moment" cookie
   video) start playing on their own when scrolled into view, loop forever,
   and have no controls, so there was no way for a user to stop the motion.
   They also ignored the operating system's "reduce motion" setting.
   This adds a visible pause/play button to every looping theme video:
   - Pausing sticks: if the user pauses, the theme's own scroll-triggered
     autoplay can't restart it.
   - Visitors with "reduce motion" turned on get no autoplay at all; they
     see the still frame and can press play themselves.
   Videos inside third-party app blocks (e.g. the Instagram feed app) are
   left alone; those belong to the app vendor. */
(function () {
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var nativePlay = HTMLMediaElement.prototype.play;

  function addVideoControl(video) {
    if (video.dataset.pauseControlAdded) return;
    if (video.controls) return;
    if (video.closest('.shopify-app-block')) return;
    video.dataset.pauseControlAdded = 'true';

    // Block any script-triggered play while the user has it paused.
    video.play = function () {
      if (video.dataset.userPaused === 'true') return Promise.resolve();
      return nativePlay.call(video);
    };
    // Backstop for attribute-based autoplay.
    video.addEventListener('play', function () {
      if (video.dataset.userPaused === 'true') video.pause();
    });

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'video-pause-btn';

    function render() {
      var paused = video.dataset.userPaused === 'true';
      btn.setAttribute('aria-pressed', paused ? 'true' : 'false');
      btn.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
      btn.innerHTML = paused ? '&#9654;' : '&#10073;&#10073;';
    }

    if (reduceMotion) {
      video.dataset.userPaused = 'true';
      video.removeAttribute('autoplay');
      video.pause();
    }
    render();

    btn.addEventListener('click', function () {
      if (video.dataset.userPaused === 'true') {
        video.dataset.userPaused = 'false';
        var p = nativePlay.call(video);
        if (p && p.catch) p.catch(function () {});
      } else {
        video.dataset.userPaused = 'true';
        video.pause();
      }
      render();
    });

    var holder = video.parentElement;
    if (window.getComputedStyle(holder).position === 'static') holder.style.position = 'relative';
    holder.appendChild(btn);
  }

  function addAllVideoControls() {
    document.querySelectorAll('video[loop]').forEach(addVideoControl);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', addAllVideoControls);
  } else {
    addAllVideoControls();
  }

  document.addEventListener('shopify:section:load', addAllVideoControls);
})();

/* Accessibility fix: WCAG 2.4.3 Focus Order / 4.1.2 Name, Role, Value
   (product page photo gallery)
   The gallery put 15 stops in the Tab order before the purchase options:
   - all 7 large photos (<li role="presentation" tabindex="0">), which do
     nothing from the keyboard and have no accessible name, and
   - the 7 thumbnail <li> wrappers, while the real, properly named buttons
     inside them ("Load image 3 in gallery view") were set to tabindex="-1".
   Live audit (Oct 8 2026): 38 Tab presses from the top of the page to reach
   "Add to bag".
   Fix: take the large photos (and the scrolling strip that holds them) out
   of the Tab order, and move focus from each thumbnail wrapper onto its own
   button. The theme's existing click/Enter handling still switches photos,
   and still marks the current one with aria-current. Result: the gallery is
   the "Skip to product information" link plus 7 named thumbnail buttons.
   The strip itself gets tabindex="-1" because Chrome automatically makes a
   scrolling container focusable once nothing inside it is focusable, which
   would otherwise put an unnamed stop back in.
   A MutationObserver re-applies this if the theme rewrites tabindex values
   when the active photo changes. */
(function () {
  function fixGallery(gallery) {
    gallery.querySelectorAll('.product__media-list > li.product__media-item').forEach(function (slide) {
            if (slide.hasAttribute('tabindex')) slide.removeAttribute('tabindex');
    });
    gallery.querySelectorAll('.product__media-list').forEach(function (strip) {
         if (strip.getAttribute('tabindex') !== '0') strip.setAttribute('tabindex', '0');
      if (strip.getAttribute('role') !== 'group') strip.setAttribute('role', 'group');
      strip.setAttribute('aria-label', 'Product images');
      strip.setAttribute('data-a11y-scroll-region', '');
    });
    gallery.querySelectorAll('.thumbnail-list__item').forEach(function (thumb) {
      if (thumb.getAttribute('tabindex') !== '-1') thumb.setAttribute('tabindex', '-1');
      var btn = thumb.querySelector('button');
      if (btn && btn.getAttribute('tabindex') !== '0') btn.setAttribute('tabindex', '0');
    });
  }

  function initGalleries() {
    document.querySelectorAll('media-gallery').forEach(function (gallery) {
      fixGallery(gallery);
      if (gallery.dataset.tabOrderFixed) return;
      gallery.dataset.tabOrderFixed = 'true';
      new MutationObserver(function () { fixGallery(gallery); })
        .observe(gallery, { attributes: true, attributeFilter: ['tabindex', 'role'], subtree: true });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGalleries);
  } else {
    initGalleries();
  }

  document.addEventListener('shopify:section:load', initGalleries);
})();

/* Accessibility + functional fix: homepage "Cookies for every moment" cards
   (snippets/product-card-custom-simple.liquid — markup and styling untouched)
   1. The 4 "Add to cart" buttons all had the same accessible name, so a
      screen reader user heard "Add to cart" four times with no product
      (WCAG 2.4.4 / 4.1.2). Each now reads "Add to cart: Focus Cookie", etc.
   2. The buttons opened a quick-view popup, but the product.quick-view
      template is missing from the theme, so the popup opened empty (dimmed
      page, nothing to add, no close button). It also skipped the Intelligems
      price test. Clicking (or pressing Enter/Space) now goes straight to the
      product page, the same as the featured-product cards above them.
   Runs in the capture phase so it handles the click before the theme's
   quick-view script sees it. */
(function () {
  function getCard(btn) { return btn.closest('.product-card-custom-simple'); }

  function labelButtons() {
    document.querySelectorAll('.product-card-custom-simple .button-quick-add-orange').forEach(function (btn) {
      var card = getCard(btn);
      var title = card && card.querySelector('.product-card-custom-simple-title');
      if (title) btn.setAttribute('aria-label', 'Add to cart: ' + title.textContent.trim());
    });
  }

  document.addEventListener('click', function (event) {
    var btn = event.target.closest('.product-card-custom-simple .button-quick-add-orange');
    if (!btn) return;
    var link = getCard(btn).querySelector('a.product-card-custom-simple-inner-wrapper');
    if (!link) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    window.location.href = link.href;
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', labelButtons);
  } else {
    labelButtons();
  }
  document.addEventListener('shopify:section:load', labelButtons);
})();

/* Accessibility fix: WCAG 2.4.4 / 1.1.1 (header logo link name)
   The desktop header logo link had no text of its own, so screen readers
   announced it using the logo images' alt text, twice:
   "Brown 'Fields Good' logo on a white background, Brown 'Fields Good'
   logo on a white background". The mobile header already named the link
   "Fields Good". This gives both header logo links the name "Fields Good"
   and marks the logo images inside them as decorative (alt=""), so the
   link is announced once, as "Fields Good, link". No visual change. */
(function () {
  function labelLogoLinks() {
    document.querySelectorAll('#shopify-section-header a[href="/"], #shopify-section-mobile-header a[href="/"]').forEach(function (link) {
      if (!link.querySelector('img')) return;
      link.setAttribute('aria-label', 'Fields Good');
      link.querySelectorAll('img').forEach(function (img) { img.setAttribute('alt', ''); });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', labelLogoLinks);
  } else {
    labelLogoLinks();
  }
  document.addEventListener('shopify:section:load', labelLogoLinks);
})();

/* Accessibility fix: homepage Instagram feed (GSC Instagram Feed app block)
   Sept 22 audit item 6. The app's markup isn't editable, so this adjusts it
   after it renders. No visual change for mouse users.
   1. Each post tile opens the post on Instagram when clicked, but the tiles
      weren't reachable by keyboard at all (WCAG 2.1.1). The tiles on screen
      are now keyboard stops (Enter or Space opens the post), named with the
      first line of the caption, e.g. "Instagram post: unbox an order with
      us, opens in a new tab" (WCAG 2.4.4 / 4.1.2). Tiles scrolled out of
      view are skipped, so focus never lands on something you can't see;
      the Next / Previous arrows bring more posts into view.
   2. The images used the full Instagram caption (up to ~670 characters of
      emoji and hashtags) as alt text. The name now lives on the tile, so
      the image itself is marked decorative (alt="") and the caption isn't
      read out twice (WCAG 1.1.1).
   3. The 15 "Run on GSC" badge links (to the app maker's site) were each a
      Tab stop. They stay visible on hover but are skipped by keyboard and
      screen readers.
   4. The previous/next arrow buttons had no name; they're now labeled. */
(function () {
  var BLOCK = '[id^="shopify-block-"][id*="gsc_instagram_feed"]';

  function shortCaption(text) {
    var line = (text || '').split('\n').map(function (s) { return s.trim(); }).filter(Boolean)[0] || '';
    line = line.replace(/\p{Extended_Pictographic}|\uFE0F|\u200D/gu, '').replace(/#\S+/g, '').replace(/\s+/g, ' ').trim();
    if (line.length > 90) line = line.slice(0, 90).replace(/\s+\S*$/, '') + '\u2026';
    return line;
  }

  // Only tiles fully inside the carousel window get a Tab stop.
  function updateTabStops(root) {
    var viewport = root.querySelector('.gfa-carousel__viewport') || root;
    var box = viewport.getBoundingClientRect();
    root.querySelectorAll('.gfa-media[role="link"]').forEach(function (media) {
      var r = media.getBoundingClientRect();
      var visible = r.width > 0 && r.left >= box.left - 2 && r.right <= box.right + 2;
      var want = visible ? '0' : '-1';
      if (media.getAttribute('tabindex') !== want) media.setAttribute('tabindex', want);
    });
  }

  function fixFeed(root) {
    if (!root.hasAttribute('role')) {
      root.setAttribute('role', 'region');
      root.setAttribute('aria-label', 'Fields Good on Instagram');
    }

    root.querySelectorAll('.gfa-copyright').forEach(function (a) {
      if (a.getAttribute('tabindex') === '-1') return;
      a.setAttribute('tabindex', '-1');
      a.setAttribute('aria-hidden', 'true');
    });

    var prev = root.querySelector('.gfa-arrow-left');
    var next = root.querySelector('.gfa-arrow-right');
    if (prev && !prev.getAttribute('aria-label')) prev.setAttribute('aria-label', 'Previous Instagram posts');
    if (next && !next.getAttribute('aria-label')) next.setAttribute('aria-label', 'Next Instagram posts');

    root.querySelectorAll('.gfa-media').forEach(function (media) {
      if (media.dataset.a11yDone) return;
      var img = media.querySelector('img');
      var caption = img && img.alt ? shortCaption(img.alt) : '';
      if (img) img.setAttribute('alt', '');
      media.setAttribute('role', 'link');
      media.setAttribute('aria-label', (caption ? 'Instagram post: ' + caption : 'Instagram post') + ', opens in a new tab');
      media.dataset.a11yDone = '1';
    });

    updateTabStops(root);
  }

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    var media = event.target.closest && event.target.closest(BLOCK + ' .gfa-media[role="link"]');
    if (!media || event.target !== media) return;
    event.preventDefault();
    (media.querySelector('.gfa-media__overlay') || media).click();
  });

  // The app block's wrapper is in the page HTML; the app fills it in after
  // load, so watch each wrapper and re-apply whenever it changes or slides.
  var observed = new WeakSet();
  function init() {
    document.querySelectorAll(BLOCK).forEach(function (root) {
      fixFeed(root);
      if (observed.has(root)) return;
      observed.add(root);
      var pending = false;
      new MutationObserver(function () {
        if (pending) return;
        pending = true;
        requestAnimationFrame(function () { pending = false; fixFeed(root); });
      }).observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class'] });
      root.addEventListener('transitionend', function () { updateTabStops(root); });
      window.addEventListener('resize', function () { updateTabStops(root); });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();

/* Accessibility fix: page structure cleanup (Sept 22 audit item 8)
   None of these change how the site looks; they change what screen readers
   and accessibility checkers (axe) see.
   1. Footer landmark (WCAG 1.3.1): the custom footer wasn't marked as a
      footer, so its menus, newsletter form and links sat outside every page
      region. It's now announced as the footer ("contentinfo").
   2. Navigation labels (1.3.1 / 2.4.1): the header, mobile and two footer
      menus were all announced as just "navigation". Now "Main", "Mobile
      menu", "Footer: Shop" and "Footer: About".
   3. Search icon (4.1.2): the header search link sat inside an element
      marked as a button (a control inside a control). It's now a single
      "Search" button; it looks and works the same, and Esc now returns
      focus to it.
   4. Comparison table (1.3.1): the first column heading was empty and the
      "Fields Good" column heading was only an unlabeled logo. They now read
      "Feature" and "Fields Good".
   5. Testimonial sliders (2.1.1): the review slider scrolls sideways but
      keyboard users couldn't reach it. It's now a named Tab stop
      ("Customer reviews") that arrow keys scroll.
   6. Landing page star ratings (1.1.1): "5 out of 5 stars" was set on a
      plain span, so screen readers read "black star" five times. They're
      now marked as images with that name.
   7. Lists pasted from Google Docs (1.3.1): list items marked
      role="presentation" weren't read as list items, and some sub-lists sat
      directly inside a list instead of inside a list item. Both are fixed
      in the page, so nothing needs re-pasting.
   8. The empty quick-view toggle left in the page now has a name.
   9. Product gallery slides were marked role="presentation" while also
      being focusable, which conflicts; the role is removed.
  10. Apps insert hidden markup ahead of the "Skip to content" link; the
      skip link is moved back to the top of the page. */
(function () {
  function setAttrs(el, attrs) {
    Object.keys(attrs).forEach(function (k) {
      if (el.getAttribute(k) !== attrs[k]) el.setAttribute(k, attrs[k]);
    });
  }

  function fixFooter() {
    var footer = document.querySelector('.shopify-section-group-footer-group.shopify-section-footer');
    if (footer && !document.querySelector('footer, [role="contentinfo"]')) footer.setAttribute('role', 'contentinfo');
  }

  function fixNavs() {
    document.querySelectorAll('#shopify-section-header nav').forEach(function (nav) {
      if (!nav.hasAttribute('aria-label')) nav.setAttribute('aria-label', 'Main');
    });
    document.querySelectorAll('.menu-drawer__content nav, #shopify-section-mega-menu-drawer nav').forEach(function (nav) {
      if (!nav.hasAttribute('aria-label')) nav.setAttribute('aria-label', 'Mobile menu');
    });
    document.querySelectorAll('.fg-footer__menu-group').forEach(function (group) {
      var nav = group.querySelector('nav');
      var heading = group.querySelector('.fg-footer__menu-heading');
      if (!nav || nav.hasAttribute('aria-label')) return;
      var name = heading ? heading.textContent.trim().toLowerCase() : '';
      name = name ? name.charAt(0).toUpperCase() + name.slice(1) : 'links';
      nav.setAttribute('aria-label', 'Footer: ' + name);
    });
  }

  // The header search is a <summary role="button"> wrapping an <a href>.
  // Make the summary the one control (named "Search", in the Tab order) and
  // turn the inner link into plain content. Clicking the icon still opens
  // the search window, because the theme listens for clicks on the summary.
  // Also: closing the search window with Esc used to drop focus to the top
  // of the page; it now goes back to the Search button.
  function fixSearchSummary() {
    document.querySelectorAll('search-modal details > summary').forEach(function (summary) {
      var link = summary.querySelector('a[href]');
      if (!link || summary.querySelector('input, button')) return;
      setAttrs(summary, { tabindex: '0', role: 'button', 'aria-label': link.getAttribute('aria-label') || 'Search' });
      link.setAttribute('data-href', link.getAttribute('href'));
      link.removeAttribute('href');
      link.setAttribute('aria-hidden', 'true');
      var details = summary.parentElement;
      if (details.dataset.a11yReturnFocus) return;
      details.dataset.a11yReturnFocus = 'true';
      details.addEventListener('toggle', function () {
        if (details.open) return;
        var active = document.activeElement;
        if (!active || active === document.body || details.contains(active)) summary.focus();
      });
    });
  }

  function fixCompareTables() {
    document.querySelectorAll('.compare-table [role="columnheader"]').forEach(function (th) {
      if (th.textContent.trim()) return;
      var img = th.querySelector('img');
      if (img) {
        var cell = th.closest('.compare-table').querySelector('.compare-table__col--highlight[data-label]');
        var label = th.getAttribute('aria-label') || (cell && cell.getAttribute('data-label')) || 'Fields Good';
        if (img.getAttribute('alt') !== label) img.setAttribute('alt', label);
        return;
      }
      var text = th.getAttribute('aria-label') || 'Feature';
      var span = document.createElement('span');
      span.className = 'visually-hidden';
      span.textContent = text;
      th.appendChild(span);
    });
  }

  function fixTestimonialSliders() {
    document.querySelectorAll('[id^="Slider-Gallery-"][id*="testimonials"]').forEach(function (slider) {
      setAttrs(slider, { tabindex: '0', 'aria-label': 'Customer reviews', 'data-a11y-scroll-region': '' });
    });
  }

  function fixStars() {
    document.querySelectorAll('.lp-stars[aria-label]').forEach(function (stars) {
      if (stars.getAttribute('role') !== 'img') stars.setAttribute('role', 'img');
    });
  }

  function fixLists() {
document.querySelectorAll('li[role="presentation"]:not(.product__media-item), li > p[role="presentation"]').forEach(function (el) {
      el.removeAttribute('role');
    });
    document.querySelectorAll('ul > ul, ul > ol, ol > ul, ol > ol').forEach(function (sub) {
      var prev = sub.previousElementSibling;
      if (prev && prev.tagName === 'LI') prev.appendChild(sub);
    });
  }

  function fixQuickViewSummary() {
    document.querySelectorAll('.quick-view__summary').forEach(function (summary) {
      if (!summary.textContent.trim() && !summary.hasAttribute('aria-label')) summary.setAttribute('aria-label', 'Quick view');
    });
  }

  function fixGallerySlides() {
    document.querySelectorAll('.product__media-list > li[role="presentation"]').forEach(function (slide) {
      slide.removeAttribute('role');
    });
  }

  // Review and popup apps insert hidden markup at the very top of the page,
  // ahead of the "Skip to content" link. Keep the skip link first.
  function fixSkipLink() {
    var skip = document.querySelector('a.skip-to-content-link');
    if (skip && document.body.firstElementChild !== skip) document.body.insertBefore(skip, document.body.firstElementChild);
  }

  function runAll() {
    fixFooter();
    fixNavs();
    fixSearchSummary();
    fixCompareTables();
    fixTestimonialSliders();
    fixStars();
    fixLists();
    fixQuickViewSummary();
    fixSkipLink();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runAll);
  } else {
    runAll();
  }
  window.addEventListener('load', fixSkipLink);
  document.addEventListener('shopify:section:load', runAll);
})();
